// WHY (#996): generic multiselect for the request-level Roads /
// Packaging dictionary selections. One component drives both surfaces
// — the dictionary differs only in the catalog query hook and the
// localized labels. Mirrors `DestinationsField` (chips + dropdown +
// add button) so the editor stays visually consistent across the
// multiselect family.
//
// Inactive options that are already part of the selection render as
// muted chips with a localized "(inactive)" suffix; they are NEVER
// offered in the dropdown so the manager cannot pick a deprecated
// option for the first time, but a historical selection round-trips
// cleanly through reopen → save without the user having to remove
// the deactivated id.

import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { Button, IconButton, Select } from '../../../ui/index.js';
import { hasPermission, useAuthStore } from '../../../stores/auth.js';
import type { DictionaryOption } from '../dictionarySelections.queries.js';

export interface DictionarySelectionFieldProps {
  selectedIds: ReadonlyArray<string>;
  onChange: (ids: string[]) => void;
  catalog: ReadonlyArray<DictionaryOption>;
  /**
   * Historical projections for ids that may have been deactivated since
   * they were first chosen. The component merges them into the byId
   * lookup so inactive selections render with their proper name and
   * inactive styling, while the dropdown still offers only `catalog`
   * (active) rows.
   */
  selectedProjections?: ReadonlyArray<DictionaryOption>;
  /**
   * i18n key suffix family inside the `requests:` namespace. The
   * component composes labels as `requests:draft.${kindKey}.<key>`.
   * Allowed values: `roads`, `packaging`.
   */
  kindKey: 'roads' | 'packaging';
  testId: string;
}

export function DictionarySelectionField({
  selectedIds,
  onChange,
  catalog,
  selectedProjections,
  kindKey,
  testId,
}: DictionarySelectionFieldProps) {
  const { t: tStrict } = useTranslation(['requests']);
  // WHY (#996): the picker labels live under
  //   `requests:draft.<kindKey>.{...}` where `kindKey` is dynamic per
  //   field (Roads vs. Packaging). i18next's strict-key generic refuses
  //   dynamic interpolation, so this site uses the untyped translator
  //   for the dynamic keys only. Static keys still flow through `tStrict`.
  const t = tStrict as unknown as (key: string, opts?: Record<string, unknown>) => string;
  const [picker, setPicker] = useState('');
  // WHY (#1152): the manage permission gates the "go to dictionary"
  //   link in the empty-state. Without it the link 404s on the
  //   PermissionGate so we render plain copy for read-only roles.
  const permissions = useAuthStore((s) => s.permissions);
  const manageCode = kindKey === 'roads' ? 'roads.manage' : 'packaging.manage';
  const canManage = hasPermission(permissions, manageCode);

  // WHY (#996): byId merges the active catalog with historical
  //   projections so inactive selections still render with their
  //   original name + inactive styling. Active rows win on key
  //   collision so a re-activated option picks up its current state.
  const byId = useMemo(() => {
    const map = new Map<string, DictionaryOption>();
    for (const p of selectedProjections ?? []) map.set(p.id, p);
    for (const c of catalog) map.set(c.id, c);
    return map;
  }, [catalog, selectedProjections]);
  const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds]);

  // WHY (#996): the dropdown offers ACTIVE catalog rows the user has
  //   not selected yet. Inactive rows that are part of the historical
  //   selection still render as chips but are NOT eligible to be
  //   re-added once removed — managers cannot pick a deprecated option
  //   for the first time.
  const remaining = useMemo(
    () => catalog.filter((c) => c.active && !selectedSet.has(c.id)),
    [catalog, selectedSet],
  );

  function addOption(id: string): void {
    if (!id || selectedSet.has(id)) return;
    onChange([...selectedIds, id]);
    setPicker('');
  }

  function removeOption(id: string): void {
    onChange(selectedIds.filter((existing) => existing !== id));
  }

  // WHY: i18next types these keys as a closed union; the dynamic
  //   `kindKey` interpolation defeats that. The runtime keys exist (see
  //   `requests` locale tree), so cast to `any` for the call-site only.
  const placeholderKey = `requests:draft.${kindKey}.pickerPlaceholder` as unknown as string;
  const addKey = `requests:draft.${kindKey}.add` as unknown as string;
  const emptyKey = `requests:draft.${kindKey}.empty` as unknown as string;
  const removeKey = `requests:draft.${kindKey}.remove` as unknown as string;
  const inactiveSuffix = t('requests:draft.dictionarySelections.inactiveSuffix');
  // WHY (#1152): distinguish "catalog has nothing active" from "all
  //   active options have already been selected". The former needs an
  //   explicit empty-state message (and a link for users with
  //   `*.manage`); the latter just hides the dropdown.
  const catalogEmpty = catalog.length === 0;
  const manageRoute = kindKey === 'roads' ? '/road-options' : '/packaging-options';
  const catalogEmptyHintKey = `requests:draft.${kindKey}.catalogEmptyHint` as unknown as string;
  const catalogEmptyLinkKey = `requests:draft.${kindKey}.catalogEmptyLink` as unknown as string;

  return (
    <div data-testid={testId}>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 8 }}>
        {selectedIds.length === 0 ? (
          <span style={{ color: 'var(--fg-3)', fontSize: 13 }}>{t(emptyKey)}</span>
        ) : (
          selectedIds.map((id) => {
            const row = byId.get(id);
            const label = row ? row.name : id;
            const isInactive = row !== undefined && !row.active;
            return (
              <span
                key={id}
                data-testid={`${testId}-chip-${id}`}
                data-inactive={isInactive ? 'true' : 'false'}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 4,
                  padding: '4px 8px',
                  border: '1px solid var(--border-1)',
                  borderRadius: 12,
                  fontSize: 13,
                  background: isInactive ? 'var(--bg-inset)' : 'var(--bg-2)',
                  color: isInactive ? 'var(--fg-3)' : 'var(--fg-1)',
                }}
              >
                {label}
                {isInactive ? ` ${inactiveSuffix}` : ''}
                <IconButton
                  icon="x"
                  size={20}
                  label={t(removeKey, { name: label })}
                  onClick={() => removeOption(id)}
                />
              </span>
            );
          })
        )}
      </div>
      {remaining.length > 0 && (
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <Select
            value={picker}
            onChange={setPicker}
            options={[
              { value: '', label: t(placeholderKey) },
              ...remaining.map((r) => ({ value: r.id, label: r.name })),
            ]}
            placeholder={t(placeholderKey)}
          />
          <Button onClick={() => addOption(picker)} disabled={!picker}>
            {t(addKey)}
          </Button>
        </div>
      )}
      {catalogEmpty && (
        <div
          data-testid={`${testId}-catalog-empty`}
          style={{
            display: 'flex',
            gap: 6,
            alignItems: 'center',
            color: 'var(--fg-3)',
            fontSize: 13,
          }}
        >
          <span>{t(catalogEmptyHintKey)}</span>
          {canManage && (
            <Link
              to={manageRoute}
              data-testid={`${testId}-catalog-empty-link`}
              style={{ color: 'var(--accent-1)' }}
            >
              {t(catalogEmptyLinkKey)}
            </Link>
          )}
        </div>
      )}
    </div>
  );
}
